import React from 'react';
import {Route, Switch} from 'react-router-dom';
import {curry} from 'ramda';
import {UserRolesEnum} from '@pluto/users';
import Landing from 'Landing/Landing';
import NotFound from 'NotFound/NotFound';
import Breadcrumbs from '../AppHeader/Breadcrumbs/BreadcrumbsConnected';
import {rolesConfig} from './RolesConfig';
import {baseRoutes} from './routes';
import {
    MapToRouteParams,
    MapToRouteValue,
    RoleConfig,
    RenderRouteProps,
} from './AppRouterTypes';

export const getRoute = (props: RenderRouteProps, route: string) => {
    const {match} = props;
    const {params} = match;
    const paramsKeys = Object.keys(params);
    const reducedRoute = paramsKeys.reduce(mapRoutesReduce(props), route);

    return paramsKeys.length ? reducedRoute : route;
};

export const filterRoutes = curry((props: RenderRouteProps, {route}: RoleConfig) => {
    return props.match.path.includes(route);
});

export const mapRoutesReduce = curry((props, path, param) => {
    return path.replace(`:${param}`, props.match.params[param]);
});

export const mapRoutes = curry((props: any, {route, ...rest}: RoleConfig) => {
    const keys = Object.keys(props.match.params);

    if (keys.length) {
        return {
            ...rest,
            route: keys.reduce(mapRoutesReduce(props), route),
        };
    }

    return {
        ...rest,
        route,
    };
});

export const renderRoutesComponent = curry(
    (Component: any, routes: RoleConfig[], props: RenderRouteProps) => {
        const mapRoutesCurried = mapRoutes(props);
        const crumbs = routes
            // Get all routes that contain the current one.
            .filter(filterRoutes(props))
            // Swap out any dynamic routes with their param values.
            // E.g. "/pizza/:pizzaId" will become "/pizza/1"
            .map(mapRoutesCurried);
        return (
            <>
                <Breadcrumbs crumbs={crumbs} />
                <Component />
            </>
        );
    }
);

export const mapToRoute = (
    {route, component}: MapToRouteParams,
    index: any,
    routes: any
): JSX.Element => {
    return (
        <Route
            exact
            key={route}
            path={route}
            render={renderRoutesComponent(component, routes)}
        />
    );
};

export const mapToRouteValue = (value: MapToRouteValue): string => value.route;

export const getDefaultRoutes = () => {
    return (
        <Switch>
            <Route path={baseRoutes.landing} component={Landing} />
        </Switch>
    );
};

export const getRolesRoutes = (roles: MapToRouteParams[]) => {
    return (
        <Switch>
            {roles.map(mapToRoute)}
            <Route path={baseRoutes.root} component={NotFound} />
        </Switch>
    );
};

export const isUnique = (
    value: MapToRouteValue,
    index: number,
    self: MapToRouteValue[]
): boolean => {
    const routes = self.map(mapToRouteValue);

    return routes.indexOf(value.route) === index;
};

export const getAllAvailableRoutes = curry((rolesConfig, roles) => {
    let routes = rolesConfig.default.slice();

    for (const role of roles) {
        routes = routes.concat(rolesConfig[role]);
    }

    return routes;
});

export default function getAllowedRoutesComponent(roles: string[]) {
    const allRoles = [
        UserRolesEnum.Default,
        UserRolesEnum.Administrator,
        UserRolesEnum.ContentOps,
        UserRolesEnum.QCOperator,
    ];
    const userRoles =
        process.env.REACT_APP_NO_AUTH && roles?.length === 0 ? allRoles : roles;
    const rolesAvailableRoutes = getAllAvailableRoutes(rolesConfig, userRoles);
    const uniqueAvailableRoutes = rolesAvailableRoutes.filter(isUnique);

    if (!userRoles.length) return getDefaultRoutes();

    return getRolesRoutes(uniqueAvailableRoutes);
}
